import type { DeviceMode, NodeV2, NodeV2Style } from '@vedalib/editor'
import { isComplexElement, isNodeV2, notEmpty } from '@vedalib/editor'

import ScrollService from 'services/Scroll/ScrollService'
import { mergeNodeStyle } from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'

import {
  COLOR_ABS,
  COLOR_FLEX,
  COLOR_MARGIN,
  COLOR_PADDING,
} from '../NodeV2Controls/NodeV2SvgInfo/constants'
import type { IGridArea } from '../formControls/Area/types'
import { safeParseArea } from '../formControls/Area/utils'
import { safeParseCoords } from '../formControls/Coords/utils'
import { safeParsePxSizes } from '../formControls/Sizes/utils'
import { ResizeControlActionUiEnum } from './enums'
import type { ICoordinates, IFullCoord, IGuide } from './types'

export const DISPLAY_OFFSET_THRESHOLD = 10
export const ABSOLUTE_OFFSET = 5

// remove exports
export const DATA_DISPLAY_ZONE = 'data-display-zone'

export const DATA_DISPLAY_ID = 'data-display-id'
export const DATA_DISPLAY_DESTINATION = 'data-display-destination'
export const DATA_DISPLAY_ELEMENT = 'data-display-element'
export const DATA_DISPLAY_DIRECTION = 'data-display-direction'
export const DATA_DISPLAY_ITEM = 'data-display-item'
export const DATA_DISPLAY_ROOT = 'data-display-root'
export const DATA_DISPLAY_DONE = 'data-display-done'
export const DATA_DISPLAY_REPEATABLE = 'data-display-repeatable'
export const DATA_DISPLAY_FLEX_FLOW = 'data-display-flex-flow'
export const DATA_DISPLAY_ROW = 'data-display-row'
export const DATA_DISPLAY_COL = 'data-display-col'
export const DATA_DISPLAY_PLACEHOLDER = 'data-display-placeholder'

export function displayProps(
  type: NodeV2 | 'navigation' | 'canvas' | 'placeholder',
  style?: NodeV2Style | null,
  options?: {
    isItem?: boolean
    isRoot?: boolean
    isSchema?: boolean
    isComplex?: boolean
  },
): Record<string, unknown> {
  if (type === 'placeholder') {
    return { [DATA_DISPLAY_PLACEHOLDER]: true }
  }

  if (type === 'navigation' || type === 'canvas') {
    return { [DATA_DISPLAY_ZONE]: type }
  }

  const { display, flexDirection, flexFlow } = style || {}
  const { id, elementId, isDone, repeatable } = type
  const { isRoot = false, isSchema = false } = options || {}
  const { isItem = false, isComplex = false } = options || {}

  return {
    [DATA_DISPLAY_ID]: id,
    ...((display || isComplex) && { [DATA_DISPLAY_DESTINATION]: display || 'complex' }),
    ...(display === 'flex' && { [DATA_DISPLAY_DIRECTION]: flexDirection || 'row' }),
    ...(flexFlow && { [DATA_DISPLAY_FLEX_FLOW]: flexFlow }),
    ...(elementId && { [DATA_DISPLAY_ELEMENT]: elementId }),
    ...(repeatable && { [DATA_DISPLAY_REPEATABLE]: true }),
    ...(isDone && { [DATA_DISPLAY_DONE]: true }),
    ...(isItem && { [DATA_DISPLAY_ITEM]: true }),
    ...(isRoot && { [DATA_DISPLAY_ROOT]: true }),
    ...(isSchema && {
      [DATA_DISPLAY_DIRECTION]: 'column',
      [DATA_DISPLAY_REPEATABLE]: true,
    }),
  }
}

export const displayRowProps = (nodeId?: string, row?: string | number) => ({
  ...(nodeId ? { [DATA_DISPLAY_ID]: nodeId } : {}),
  ...(row ? { [DATA_DISPLAY_ROW]: row } : {}),
})

export const displayColProps = (nodeId?: string, col?: string | number) => ({
  ...(nodeId ? { [DATA_DISPLAY_ID]: nodeId } : {}),
  ...(col ? { [DATA_DISPLAY_COL]: col } : {}),
})

export const isOffset = (
  initial: ICoordinates | null,
  current: React.MouseEvent | MouseEvent,
  offset = DISPLAY_OFFSET_THRESHOLD,
) =>
  initial &&
  (Math.abs(initial.x - current.clientX) > offset || Math.abs(initial.y - current.clientY) > offset)

const getElements = ({ x, y }: ICoordinates) => {
  const elements = document.elementsFromPoint(x, y)
  const zone = elements.find((el) => el.hasAttribute(DATA_DISPLAY_ZONE))
  const destination = elements.find((el) => {
    const type = el.getAttribute(DATA_DISPLAY_DESTINATION)
    const isRoot = el.getAttribute(DATA_DISPLAY_ROOT)
    const isDone = el.getAttribute(DATA_DISPLAY_DONE)
    if (type && (isRoot || !isDone)) {
      return true
    }
    return false
  })
  const row = elements.find((el) => el.hasAttribute(DATA_DISPLAY_ROW))
  const col = elements.find((el) => el.hasAttribute(DATA_DISPLAY_COL))
  return { zone, destination, row, col }
}

export const findNodeRef = (id?: string) =>
  ScrollService.canvas.ref?.querySelector(`[${DATA_DISPLAY_ID}='${id}']`)

export const collectDndData = (block: Block, { x, y }: ICoordinates) => {
  const els = getElements({ x, y })
  const zone = els.zone?.getAttribute(DATA_DISPLAY_ZONE) as 'navigation' | 'canvas' | undefined

  const destinationId = els.destination?.getAttribute(DATA_DISPLAY_ID)
  const destinationType = els.destination?.getAttribute(DATA_DISPLAY_DESTINATION)
  const destinationRepeatable = els.destination?.getAttribute(DATA_DISPLAY_REPEATABLE) === 'true'

  if (destinationType === 'flex' || destinationRepeatable /* || destinationComplex */) {
    const destinationNode = (destinationId && block.schema.nodes[destinationId]) as NodeV2 | null
    const direction = els.destination?.getAttribute(DATA_DISPLAY_DIRECTION) || null
    const isReverse = direction === 'row-reverse' || direction === 'column-reverse'
    const element = destinationNode?.elementId ? block.elements[destinationNode.elementId] : null
    const childrenSelector = (
      element && isComplexElement(element)
        ? element.value.items.map(({ value }) => value)
        : destinationNode?.children
    )
      ?.map((childId: string) => `[${DATA_DISPLAY_ID}="${childId}"]`)
      .join(',')
    let children =
      els.destination && childrenSelector
        ? Array.from(els.destination.querySelectorAll(childrenSelector))
        : []

    children = (isReverse ? children.slice().reverse() : children).filter(
      (ref) => ref.getAttribute(DATA_DISPLAY_FLEX_FLOW) !== 'absolute',
    )

    const destRect = els.destination?.getBoundingClientRect()
    const childrenRects = children.map((child) => child.getBoundingClientRect())
    let index: number | null = null
    let insertIndex: number | null = 0
    let insertX: number | null = null
    let insertY: number | null = null
    if (destRect) {
      if (children.length === 0) {
        insertIndex = 0
        insertX = destRect.width / 2
        insertY = destRect.height / 2
      } else {
        index = getClosestIndex(x, y, childrenRects)
        insertIndex = getInsertIndex(x, y, childrenRects, index, direction)

        if (typeof insertIndex === 'number' && typeof index === 'number') {
          const closest = childrenRects[index]
          const prevEl = childrenRects[insertIndex - 1] as DOMRect | null
          const nextEl = childrenRects[insertIndex] as DOMRect | null

          if (!direction || direction === 'row' || direction === 'row-reverse') {
            const anotherRow = (prevEl?.right || 0) > (nextEl?.left || 0)
            const x1 = prevEl
              ? anotherRow
                ? index !== insertIndex
                  ? closest.right
                  : destRect.left
                : prevEl.right
              : destRect.left
            const x2 = nextEl
              ? anotherRow
                ? index !== insertIndex
                  ? destRect.right
                  : closest.left
                : nextEl.left
              : destRect.right
            const y1 = anotherRow
              ? closest.top + closest.height / 2
              : prevEl
                ? prevEl.top + prevEl.height / 2
                : closest.top + closest.height / 2
            const y2 = anotherRow
              ? closest.top + closest.height / 2
              : nextEl
                ? nextEl.top + nextEl.height / 2
                : closest.top + closest.height / 2

            insertX = (x1 + x2) / 2 - destRect.left
            insertY = ((y1 || y2) + (y2 || y1)) / 2 - destRect.top
          } else if (direction === 'column' || direction === 'column-reverse') {
            const anotherColumn = (prevEl?.bottom || 0) > (nextEl?.top || 0)

            const x1 = anotherColumn
              ? closest.left + closest.width / 2
              : prevEl
                ? prevEl.left + prevEl.width / 2
                : closest.left + closest.width / 2
            const x2 = anotherColumn
              ? closest.left + closest.width / 2
              : nextEl
                ? nextEl.left + nextEl.width / 2
                : closest.left + closest.width / 2

            const y1 = prevEl
              ? anotherColumn
                ? index !== insertIndex
                  ? closest.bottom
                  : destRect.top
                : prevEl.bottom
              : destRect.top
            const y2 = nextEl
              ? anotherColumn
                ? index !== insertIndex
                  ? destRect.bottom
                  : closest.top
                : nextEl.top
              : destRect.bottom
            insertX = (x1 + x2) / 2 - destRect.left
            insertY = ((y1 || y2) + (y2 || y1)) / 2 - destRect.top
          }
        }
      }
    }

    return {
      zone,
      destinationId,
      insertIndex:
        typeof insertIndex === 'number'
          ? isReverse
            ? children.length - insertIndex
            : insertIndex
          : null,
      insertX,
      insertY,
      top: y - (destRect?.top || 0),
      left: x - (destRect?.left || 0),
      bottom: y - (destRect?.bottom || 0),
      right: x - (destRect?.right || 0),
    }
  }

  if (destinationType === 'grid' && !destinationRepeatable) {
    const row = Number(els.row?.getAttribute(DATA_DISPLAY_ROW)) || null
    const col = Number(els.col?.getAttribute(DATA_DISPLAY_COL)) || null

    return { zone, destinationId, row, col }
  }

  if (destinationType === 'absolute') {
    const destRect = els.destination?.getBoundingClientRect()

    return {
      zone,
      destinationId,
      top: y - (destRect?.top || 0),
      left: x - (destRect?.left || 0),
      bottom: y - (destRect?.bottom || 0),
      right: x - (destRect?.right || 0),
    }
  }

  return { zone, destinationId: null }
}

export const findRow = ({ x, y }: ICoordinates, nodeId: string /* shallow = false */) => {
  const nodes = document.elementsFromPoint(x, y).filter((i) => i.hasAttribute(DATA_DISPLAY_ID))
  const node = nodes.find(
    (i) => i.getAttribute(DATA_DISPLAY_ID) === nodeId && i.hasAttribute(DATA_DISPLAY_ROW),
  )

  return Number(node?.getAttribute(DATA_DISPLAY_ROW)) || -1
}

export const findCol = ({ x, y }: ICoordinates, nodeId: string /* shallow = false */) => {
  const nodes = document.elementsFromPoint(x, y).filter((i) => i.hasAttribute(DATA_DISPLAY_ID))
  const node = nodes.find(
    (i) => i.getAttribute(DATA_DISPLAY_ID) === nodeId && i.hasAttribute(DATA_DISPLAY_COL),
  )

  return Number(node?.getAttribute(DATA_DISPLAY_COL)) || -1
}

export const CALCULATE_AREA: Record<
  ResizeControlActionUiEnum,
  (point: ICoordinates, current: IGridArea, nodeId: string) => IGridArea
> = {
  TOP: (point, current, nodeId) => ({
    ...current,
    rowStart: findRow(point, nodeId) || current.rowStart,
  }),
  RIGHT: (point, current, nodeId) => ({
    ...current,
    colEnd: findCol(point, nodeId) + 1 || current.colEnd,
  }),
  BOTTOM: (point, current, nodeId) => ({
    ...current,
    rowEnd: findRow(point, nodeId) + 1 || current.rowEnd,
  }),
  LEFT: (point, current, nodeId) => ({
    ...current,
    colStart: findCol(point, nodeId) || current.colStart,
  }),
  TOP_LEFT: (point, current, nodeId) => ({
    ...current,
    rowStart: findRow(point, nodeId) || current.rowStart,
    colStart: findCol(point, nodeId) || current.colStart,
  }),
  TOP_RIGHT: (point, current, nodeId) => ({
    ...current,
    colEnd: findCol(point, nodeId) + 1 || current.colEnd,
    rowStart: findRow(point, nodeId) || current.rowStart,
  }),
  BOTTOM_RIGHT: (point, current, nodeId) => ({
    ...current,
    colEnd: findCol(point, nodeId) + 1 || current.colEnd,
    rowEnd: findRow(point, nodeId) + 1 || current.rowEnd,
  }),
  BOTTOM_LEFT: (point, current, nodeId) => ({
    ...current,
    colStart: findCol(point, nodeId) || current.colStart,
    rowEnd: findRow(point, nodeId) + 1 || current.rowEnd,
  }),
}

const getY = (point: ICoordinates, nodeId: string, reverse?: boolean): number | null => {
  const node = findNodeRef(nodeId)
  if (!node) {
    return null
  }
  const { y, height } = node.getBoundingClientRect()
  if (reverse) {
    return height - (point.y - y)
  }
  return point.y - y
}

const getX = (point: ICoordinates, nodeId: string, reverse?: boolean): number | null => {
  const node = findNodeRef(nodeId)
  if (!node) {
    return null
  }
  const { x, width } = node.getBoundingClientRect()
  if (reverse) {
    return width - (point.x - x)
  }
  return point.x - x
}

export const CALCULATE_COORD: Record<
  ResizeControlActionUiEnum,
  (
    point: ICoordinates,
    current: { top: number; left: number; bottom: number; right: number },
    nodeId: string,
  ) => { top: number; left: number; bottom: number; right: number }
> = {
  TOP: (point, current, nodeId) => ({
    ...current,
    top: getY(point, nodeId) ?? current.top,
  }),
  RIGHT: (point, current, nodeId) => ({
    ...current,
    right: getX(point, nodeId, true) ?? current.right,
  }),
  BOTTOM: (point, current, nodeId) => ({
    ...current,
    bottom: getY(point, nodeId, true) ?? current.bottom,
  }),
  LEFT: (point, current, nodeId) => ({
    ...current,
    left: getX(point, nodeId) ?? current.left,
  }),
  TOP_LEFT: (point, current, nodeId) => ({
    ...current,
    top: getY(point, nodeId) ?? current.top,
    left: getX(point, nodeId) ?? current.left,
  }),
  TOP_RIGHT: (point, current, nodeId) => ({
    ...current,
    right: getX(point, nodeId, true) ?? current.right,
    top: getY(point, nodeId) ?? current.top,
  }),
  BOTTOM_RIGHT: (point, current, nodeId) => ({
    ...current,
    right: getX(point, nodeId, true) ?? current.right,
    bottom: getY(point, nodeId, true) ?? current.bottom,
  }),
  BOTTOM_LEFT: (point, current, nodeId) => ({
    ...current,
    left: getX(point, nodeId) ?? current.left,
    bottom: getY(point, nodeId, true) ?? current.bottom,
  }),
}

const getClosestIndex = (x: number, y: number, rects: DOMRect[]): number | null => {
  let closest = null
  let minDist = Infinity
  rects.forEach((r, i) => {
    if (r.bottom > y && r.top < y && r.left < x && r.right > x) {
      minDist = 0
      closest = i
    }
    const corners = [
      { x: r.left, y: r.top },
      { x: r.right, y: r.top },
      { x: r.right, y: r.bottom },
      { x: r.left, y: r.bottom },
    ]
    // const cx = r.left + r.width / 2
    // const cy = r.top + r.height / 2
    const dist = Math.min(...corners.map(({ x: cx, y: cy }) => Math.hypot(cx - x, cy - y)))
    if (dist < minDist) {
      minDist = dist
      closest = i
    }
  })
  return closest
}

const getInsertIndex = (
  x: number,
  y: number,
  rects: DOMRect[],
  index: number | null,
  direction: string | null,
) => {
  if (typeof index !== 'number') {
    return null
  }
  if (!direction || direction === 'row' || direction === 'row-reverse') {
    const r = rects[index]
    const cx = r.left + r.width / 2
    if (x > cx) {
      return index + 1
    }
    return index
  }

  if (direction === 'column' || direction === 'column-reverse') {
    const r = rects[index]
    const cy = r.top + r.height / 2

    if (y > cy) {
      return index + 1
    }
    return index
  }

  return index
}

export const getGroupArea = (nodes: NodeV2[], device: DeviceMode) => {
  const nodeStyles = nodes.filter(isNodeV2).map((node) => mergeNodeStyle(node.style, device))
  return {
    rowStart: Math.min(...nodeStyles.map((style) => safeParseArea(style.gridArea).rowStart)),
    rowEnd: Math.max(...nodeStyles.map((style) => safeParseArea(style.gridArea).rowEnd)),
    colStart: Math.min(...nodeStyles.map((style) => safeParseArea(style.gridArea).colStart)),
    colEnd: Math.max(...nodeStyles.map((style) => safeParseArea(style.gridArea).colEnd)),
  }
}

export const getGroupCoords = (node: NodeV2 | null, nodes: NodeV2[], device: DeviceMode) => {
  const pBox = findNodeRef(node?.id || '')?.getBoundingClientRect()
  if (!pBox) {
    return {
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    }
  }
  const boxes = nodes
    .map((node) => {
      const box = findNodeRef(node.id)?.getBoundingClientRect()
      const style = mergeNodeStyle(node.style, device)
      if (box) {
        return { box, margin: safeParsePxSizes(style.margin) }
      }
      return null
    })
    .filter(notEmpty)

  return {
    top: Math.min(...boxes.map(({ box, margin }) => box.top - pBox.top - (margin[0] || 0))),
    left: Math.min(...boxes.map(({ box, margin }) => box.left - pBox.left - (margin[3] || 0))),
    bottom: Math.min(
      ...boxes.map(
        ({ box, margin }) => pBox.height - (box.top + box.height - pBox.top) - (margin[2] || 0),
      ),
    ),
    right: Math.min(
      ...boxes.map(
        ({ box, margin }) => pBox.width - (box.left + box.width - pBox.left) - (margin[1] || 0),
      ),
    ),
  }
}

export function remapNodesRelativeToArea(
  areas: IGridArea[],
  oldArea: IGridArea,
  newArea: IGridArea,
): IGridArea[] {
  const oldHeight = oldArea.rowEnd - oldArea.rowStart
  const oldWidth = oldArea.colEnd - oldArea.colStart

  const newHeight = newArea.rowEnd - newArea.rowStart
  const newWidth = newArea.colEnd - newArea.colStart

  return areas.map((area) => {
    const relativeRowStart = (area.rowStart - oldArea.rowStart) / oldHeight
    const relativeRowEnd = (area.rowEnd - oldArea.rowStart) / oldHeight

    const relativeColStart = (area.colStart - oldArea.colStart) / oldWidth
    const relativeColEnd = (area.colEnd - oldArea.colStart) / oldWidth

    const rowStart = Math.round(newArea.rowStart + relativeRowStart * newHeight)
    const rowEnd = Math.round(newArea.rowStart + relativeRowEnd * newHeight)

    const colStart = Math.round(newArea.colStart + relativeColStart * newWidth)
    const colEnd = Math.round(newArea.colStart + relativeColEnd * newWidth)

    if (rowEnd - rowStart < 1 || colEnd - colStart < 1) {
      const offsetRow = area.rowStart - oldArea.rowStart
      const offsetCol = area.colStart - oldArea.colStart
      const height = area.rowEnd - area.rowStart
      const width = area.colEnd - area.colStart

      return {
        rowStart: newArea.rowStart + offsetRow,
        rowEnd: newArea.rowStart + offsetRow + height,
        colStart: newArea.colStart + offsetCol,
        colEnd: newArea.colStart + offsetCol + width,
      }
    }

    return { rowStart, rowEnd, colStart, colEnd }
  })
}

// Вспомогательный интерфейс для расчетов в абсолютных пикселях (x, y от 0,0 родителя)
interface IAbsRect {
  x: number
  y: number
  width: number
  height: number
}

export function remapNodesRelativeToCoords(
  nodeBox: DOMRect, // Размер родительского контейнера
  type: ResizeControlActionUiEnum,
  coords: IFullCoord[],
  oldCoord: { top: number; left: number; bottom: number; right: number },
  newCoord: { top: number; left: number; bottom: number; right: number },
): IFullCoord[] {
  // 1. Определяем геометрию рамки выделения (Selection Box)
  const oldSelection: IAbsRect = {
    x: oldCoord.left,
    y: oldCoord.top,
    width: nodeBox.width - oldCoord.left - oldCoord.right,
    height: nodeBox.height - oldCoord.top - oldCoord.bottom,
  }

  const newSelection: IAbsRect = {
    x: newCoord.left,
    y: newCoord.top,
    width: nodeBox.width - newCoord.left - newCoord.right,
    height: nodeBox.height - newCoord.top - newCoord.bottom,
  }

  // Коэффициенты масштабирования
  const scaleX = oldSelection.width === 0 ? 1 : newSelection.width / oldSelection.width
  const scaleY = oldSelection.height === 0 ? 1 : newSelection.height / oldSelection.height

  return coords.map((node) => {
    const [offTop, offRight, offBottom, offLeft] = node.offset

    // 2. Рассчитываем ПОЛНЫЕ (Visual) размеры элемента до трансформации
    // Поскольку offset "съедает" место внутри или снаружи, при ресайзе мы оперируем полной занимаемой площадью.
    const currentTotalWidth = node.width + offLeft + offRight
    const currentTotalHeight = node.height + offTop + offBottom

    // 3. Вычисляем абсолютные координаты (Visual X/Y) элемента
    // Координаты уже учитывают offset, поэтому мы просто ищем позицию левого верхнего угла "коробки"
    let nodeAbsX = 0
    let nodeAbsY = 0

    if (typeof node.left === 'number') {
      nodeAbsX = node.left
    } else if (typeof node.right === 'number') {
      // Если привязано к правому краю: ШиринаРодителя - ОтступСправа - ПолнаяШиринаЭлемента
      nodeAbsX = nodeBox.width - node.right - currentTotalWidth
    }

    if (typeof node.top === 'number') {
      nodeAbsY = node.top
    } else if (typeof node.bottom === 'number') {
      // Если привязано к низу: ВысотаРодителя - ОтступСнизу - ПолнаяВысотаЭлемента
      nodeAbsY = nodeBox.height - node.bottom - currentTotalHeight
    }

    // 4. Вычисляем НОВЫЕ координаты и размеры (Масштабирование)

    // А. Позиция (Translate)
    // Считаем, где визуальный блок был относительно старого выделения
    const relativeX = nodeAbsX - oldSelection.x
    const relativeY = nodeAbsY - oldSelection.y

    const newAbsX = newSelection.x + relativeX * scaleX
    const newAbsY = newSelection.y + relativeY * scaleY

    // Б. Размер (Scale)
    // Масштабируем ПОЛНЫЙ размер
    const newTotalWidth = currentTotalWidth * scaleX
    const newTotalHeight = currentTotalHeight * scaleY

    // В. Восстанавливаем внутренний width/height
    // Вычитаем фиксированные офсеты из нового масштабированного размера
    const finalWidth = newTotalWidth - offLeft - offRight
    const finalHeight = newTotalHeight - offTop - offBottom

    // 5. Логика привязок (Anchoring) - определяем left/right/top/bottom

    let useLeft = false
    let useTop = false

    // Горизонталь
    switch (type) {
      case ResizeControlActionUiEnum.LEFT:
      case ResizeControlActionUiEnum.TOP_LEFT:
      case ResizeControlActionUiEnum.BOTTOM_LEFT:
        useLeft = true
        break
      case ResizeControlActionUiEnum.RIGHT:
      case ResizeControlActionUiEnum.TOP_RIGHT:
      case ResizeControlActionUiEnum.BOTTOM_RIGHT:
        useLeft = false
        break
      default:
        useLeft = typeof node.left === 'number'
        break
    }

    // Вертикаль
    switch (type) {
      case ResizeControlActionUiEnum.TOP:
      case ResizeControlActionUiEnum.TOP_LEFT:
      case ResizeControlActionUiEnum.TOP_RIGHT:
        useTop = true
        break
      case ResizeControlActionUiEnum.BOTTOM:
      case ResizeControlActionUiEnum.BOTTOM_LEFT:
      case ResizeControlActionUiEnum.BOTTOM_RIGHT:
        useTop = false
        break
      default:
        useTop = typeof node.top === 'number'
        break
    }

    // 6. Формируем результат
    const result: IFullCoord = {
      width: finalWidth,
      height: finalHeight,
      offset: [...node.offset], // Оставляем как есть
    }

    // Записываем X
    if (useLeft) {
      result.left = newAbsX
    } else {
      // right считаем от правого края визуального блока (newAbsX + newTotalWidth)
      result.right = nodeBox.width - (newAbsX + newTotalWidth)
    }

    // Записываем Y
    if (useTop) {
      result.top = newAbsY
    } else {
      // bottom считаем от нижнего края визуального блока (newAbsY + newTotalHeight)
      result.bottom = nodeBox.height - (newAbsY + newTotalHeight)
    }

    return result
  })
}

const getMargins = (
  node: NodeV2,
  size: { width: number; height: number },
  style: NodeV2Style | null,
) => {
  const offset = safeParsePxSizes(style?.margin)
  const offsetTop = offset[0] || 0
  const offsetRight = offset[1] || 0
  const offsetBottom = offset[2] || 0
  const offsetLeft = offset[3] || 0
  return [
    offsetTop
      ? {
          id: `${node.id}-margin-top`,
          color: COLOR_MARGIN,
          x1: 0,
          y1: 0,
          x2: size.width + offsetLeft + offsetRight,
          y2: 0,
          alwaysShow: true,
        }
      : null,
    offsetLeft
      ? {
          id: `${node.id}-margin-left`,
          color: COLOR_MARGIN,
          x1: 0,
          y1: 0,
          x2: 0,
          y2: size.height + offsetTop + offsetBottom,
          alwaysShow: true,
        }
      : null,
    offsetRight
      ? {
          id: `${node.id}-margin-right`,
          color: COLOR_MARGIN,
          x1: size.width + offsetLeft + offsetRight,
          y1: 0,
          x2: size.width + offsetLeft + offsetRight,
          y2: size.height + offsetTop + offsetBottom,
          alwaysShow: true,
        }
      : null,
    offsetBottom
      ? {
          id: `${node.id}-margin-bottom`,
          color: COLOR_MARGIN,
          x1: 0,
          y1: size.height + offsetTop + offsetBottom,
          x2: size.width + offsetLeft + offsetRight,
          y2: size.height + offsetTop + offsetBottom,
          alwaysShow: true,
        }
      : null,
  ].filter(notEmpty)
}

const getPaddings = (
  node: NodeV2,
  size: { width: number; height: number },
  style: NodeV2Style | null,
) => {
  const offset = safeParsePxSizes(style?.margin)
  const offsetTop = offset[0] || 0
  const offsetLeft = offset[3] || 0
  const padding = safeParsePxSizes(style?.padding)
  const paddingTop = padding[0] || 0
  const paddingRight = padding[1] || 0
  const paddingBottom = padding[2] || 0
  const paddingLeft = padding[3] || 0

  return [
    paddingTop
      ? {
          id: `${node.id}-padding-top`,
          color: COLOR_PADDING,
          x1: offsetLeft,
          y1: offsetTop,
          x2: size.width + offsetLeft,
          y2: offsetTop,
          alwaysShow: true,
        }
      : null,
    paddingLeft
      ? {
          id: `${node.id}-padding-left`,
          color: COLOR_PADDING,
          x1: offsetLeft,
          y1: offsetTop,
          x2: offsetLeft,
          y2: size.height + offsetTop,
          alwaysShow: true,
        }
      : null,
    paddingRight
      ? {
          id: `${node.id}-padding-right`,
          color: COLOR_PADDING,
          x1: size.width + offsetLeft,
          y1: offsetTop,
          x2: size.width + offsetLeft,
          y2: size.height + offsetTop,
          alwaysShow: true,
        }
      : null,
    paddingBottom
      ? {
          id: `${node.id}-padding-bottom`,
          color: COLOR_PADDING,
          x1: offsetLeft,
          y1: size.height + offsetTop,
          x2: size.width + offsetLeft,
          y2: size.height + offsetTop,
          alwaysShow: true,
        }
      : null,
  ].filter(notEmpty)
}

const getContent = (
  node: NodeV2,
  size: { width: number; height: number },
  style: NodeV2Style | null,
) => {
  const offset = safeParsePxSizes(style?.margin)
  const offsetTop = offset[0] || 0
  const offsetLeft = offset[3] || 0
  const padding = safeParsePxSizes(style?.padding)
  const paddingTop = padding[0] || 0
  const paddingRight = padding[1] || 0
  const paddingBottom = padding[2] || 0
  const paddingLeft = padding[3] || 0
  return [
    {
      id: `${node.id}-content-top`,
      color: COLOR_ABS,
      x1: offsetLeft + paddingLeft,
      y1: offsetTop + paddingTop,
      x2: offsetLeft + size.width - paddingRight,
      y2: offsetTop + paddingTop,
      alwaysShow: true,
    },
    {
      id: `${node.id}-content-left`,
      color: COLOR_ABS,
      x1: offsetLeft + paddingLeft,
      y1: offsetTop + paddingTop,
      x2: offsetLeft + paddingLeft,
      y2: offsetTop + size.height - paddingBottom,
      alwaysShow: true,
    },
    {
      id: `${node.id}-content-right`,
      color: COLOR_ABS,
      x1: offsetLeft + size.width - paddingRight,
      y1: offsetTop + paddingTop,
      x2: offsetLeft + size.width - paddingRight,
      y2: offsetTop + size.height - paddingBottom,
      alwaysShow: true,
    },
    {
      id: `${node.id}-content-bottom`,
      color: COLOR_ABS,
      x1: offsetLeft + paddingLeft,
      y1: offsetTop + size.height - paddingBottom,
      x2: offsetLeft + size.width - paddingRight,
      y2: offsetTop + size.height - paddingBottom,
      alwaysShow: true,
    },
  ]
}

export const calcAbsoluteGuides = (
  node: NodeV2,
  block: Block,
  device: DeviceMode,
  size: { width: number; height: number },
  style: NodeV2Style,
  targetIds?: string[],
): IGuide[] => {
  const offset = safeParsePxSizes(style.margin)
  const offsetTop = offset[0] || 0
  const offsetRight = offset[1] || 0
  const offsetBottom = offset[2] || 0
  const offsetLeft = offset[3] || 0

  return [
    ...getMargins(node, size, style),
    ...getPaddings(node, size, style),
    ...getContent(node, size, style),
    {
      id: `${node.id}-vertical-center`,
      color: COLOR_FLEX,
      x1: offsetLeft + size.width / 2,
      y1: 0,
      x2: offsetLeft + size.width / 2,
      y2: size.height + offsetTop + offsetBottom,
    },
    {
      id: `${node.id}-horizontal-center`,
      color: COLOR_FLEX,
      x1: 0,
      y1: offsetTop + size.height / 2,
      x2: size.width + offsetLeft + offsetRight,
      y2: offsetTop + size.height / 2,
    },
    ...node.children.reduce<IGuide[]>((acc, childId) => {
      const child = block.schema.nodes[childId]
      if (!isNodeV2(child) || targetIds?.includes(child.id)) {
        return acc
      }

      const childStyle = mergeNodeStyle(child.style, device)
      const margins = safeParsePxSizes(childStyle.margin)
      const marginTop = margins[0] || 0
      const marginRight = margins[1] || 0
      const marginBottom = margins[2] || 0
      const marginLeft = margins[3] || 0
      const ref = findNodeRef(child.id)
      const nodeRef = findNodeRef(node.id)
      const nodeBox = nodeRef?.getBoundingClientRect()
      const box = ref?.getBoundingClientRect()
      const coords = {
        top: box && nodeBox ? box.top - nodeBox.top - offsetTop : undefined,
        left: box && nodeBox ? box.left - nodeBox.left - offsetLeft : undefined,
        bottom:
          box && nodeBox
            ? nodeBox.height - (box.top - nodeBox.top + box.height) - offsetBottom
            : undefined,
        right:
          box && nodeBox
            ? nodeBox.width - (box.left - nodeBox.left + box.width) - offsetRight
            : undefined,
      }
      const { top, left, bottom, right } = safeParseCoords(childStyle.coords, coords)

      const childTop =
        top || size.height - (bottom || 0) - ((box?.height || 0) + marginTop + marginBottom)
      const childLeft =
        left || size.width - (right || 0) - ((box?.width || 0) + marginLeft + marginRight)
      const childRight =
        right || size.width - (left || 0) - ((box?.width || 0) + marginLeft + marginRight)
      const childBottom =
        bottom || size.height - (top || 0) - ((box?.height || 0) + marginTop + marginBottom)

      acc.push({
        id: `${child.id}-top`,
        color: COLOR_ABS,
        x1: offsetLeft,
        y1: childTop + offsetTop,
        x2: offsetLeft + size.width,
        y2: childTop + offsetTop,
      })
      acc.push({
        id: `${child.id}-left`,
        color: COLOR_ABS,
        x1: childLeft + offsetLeft,
        y1: offsetTop,
        x2: childLeft + offsetLeft,
        y2: offsetTop + size.height,
      })
      const rightPos = size.width + offsetLeft + offsetRight - childRight
      acc.push({
        id: `${child.id}-right`,
        color: COLOR_ABS,
        x1: rightPos - offsetRight,
        y1: offsetTop,
        x2: rightPos - offsetRight,
        y2: offsetTop + size.height,
      })
      const bottomPos = size.height + offsetTop + offsetBottom - childBottom
      acc.push({
        id: `${child.id}-bottom`,
        color: COLOR_ABS,
        x1: offsetLeft,
        y1: bottomPos - offsetBottom,
        x2: offsetLeft + size.width,
        y2: bottomPos - offsetBottom,
      })

      acc.push({
        id: `${child.id}-vertical-center`,
        color: COLOR_ABS,
        x1: childLeft + offsetLeft + ((box?.width || 0) + marginLeft + marginRight) / 2,
        y1: offsetTop,
        x2: childLeft + offsetLeft + ((box?.width || 0) + marginLeft + marginRight) / 2,
        y2: offsetTop + size.height,
      })
      acc.push({
        id: `${child.id}-horizontal-center`,
        color: COLOR_ABS,
        x1: offsetLeft,
        y1: childTop + offsetTop + ((box?.height || 0) + marginTop + marginBottom) / 2,
        x2: offsetLeft + size.width,
        y2: childTop + offsetTop + ((box?.height || 0) + marginTop + marginBottom) / 2,
      })

      return acc
    }, []),
  ]
}
